import { format, parseISO } from 'date-fns';
import type { Event } from '../types/contentful';

// Events are all-day when their stored Contentful `date` carries no time part
// (`2026-10-30` rather than `2026-10-30T00:00-04:00`). An all-day event names a
// calendar day, not an instant, so it must render as that same day in every
// time zone — a timestamp at midnight Eastern lands on the previous day for
// anyone further west (WIPE-940).
const DATE_INPUT_FORMAT = 'yyyy-MM-dd';

export function isAllDayEvent(date: string | undefined | null): boolean {
  return !!date && !date.includes('T');
}

// Parse a stored event date for display. `parseISO` anchors a date-only string
// at local midnight, where `new Date()` would treat it as UTC midnight and shift
// the day backwards west of UTC.
export function parseEventDate(date: string): Date {
  return parseISO(date);
}

// Format the time of day for a stored event date, or null when the event is
// all-day and has no meaningful time to show.
export function formatEventTime(date: string | undefined | null): string | null {
  if (!date || isAllDayEvent(date)) return null;
  return parseEventDate(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Format a Date as a `yyyy-MM-dd` string in local time. Use this instead of
// `toISOString().split('T')[0]`, which yields the UTC day and so names the wrong
// date for local times late in the day.
export function toLocalDateString(date: Date): string {
  return format(date, DATE_INPUT_FORMAT);
}

// Format a stored date as the value a `date` input expects. Returns '' for an
// empty date. There is no inverse helper: such an input's value is already the
// stored representation of an all-day event.
export function isoToDateInput(date: string | undefined | null): string {
  return date ? toLocalDateString(parseEventDate(date)) : '';
}

// Order the events within a single day. All-day events have no time to sort by,
// so they lead. Undated events sort last; day and week views filter them out
// before sorting, but the comparator shouldn't depend on that.
export function compareEventTimes(a: Event, b: Event): number {
  if (!a.date || !b.date) return a.date ? -1 : b.date ? 1 : 0;
  const aAllDay = isAllDayEvent(a.date);
  const bAllDay = isAllDayEvent(b.date);
  if (aAllDay !== bAllDay) return aAllDay ? -1 : 1;
  if (aAllDay) return 0;
  return parseEventDate(a.date).getTime() - parseEventDate(b.date).getTime();
}
